import { useId } from 'react';

import classNames from 'classnames';

import styles from './AnnotationArrow.module.css';

import type { Color, ColorName } from '../../styles';
import { getColor } from '../../styles';
import {
  CURVED_DESKTOP_PATH,
  CURVED_MOBILE_PATH,
  MEDIUM_DESKTOP_PATH,
  MEDIUM_MOBILE_PATH,
  SHORT_DESKTOP_PATH,
  SHORT_MOBILE_PATH,
} from './paths';

export type AnnotationArrowVariant = 'short' | 'curved' | 'medium';

export type AnnotationArrowProps = {
  /** The arrow style variant. */
  variant: AnnotationArrowVariant;
  /** Rotation in degrees. */
  rotate?: number;
  /** Arrow height in pixels or CSS value. Defaults to 24. */
  size?: number | string;
  /** Arrow color. Defaults to Carrot500 (#F2674B). */
  baseColor?: Color | ColorName;
  /** Additional CSS class for the wrapper. */
  className?: string;
};

export function AnnotationArrow({
  variant,
  rotate = 0,
  size = 24,
  baseColor = 'Carrot500',
  className,
}: AnnotationArrowProps): JSX.Element {
  const id = useId();
  const color = getColor(baseColor);
  const wrapperStyle =
    rotate !== 0 ? { transform: `rotate(${rotate}deg)` } : undefined;
  const sizeStyle = size
    ? { height: typeof size === 'number' ? `${size}px` : size }
    : undefined;

  const svgProps = {
    'aria-hidden': true as const,
    fill: 'none',
    style: sizeStyle,
  };

  return (
    <span className={classNames(styles.root, className)} style={wrapperStyle}>
      {variant === 'short' && (
        <>
          <svg {...svgProps} viewBox="0 0 25 32" className={styles.mobile}>
            <path
              d={SHORT_MOBILE_PATH}
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <svg {...svgProps} viewBox="0 0 26 33" className={styles.desktop}>
            <path
              d={SHORT_DESKTOP_PATH}
              stroke={color}
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </>
      )}

      {variant === 'curved' && (
        <>
          <svg {...svgProps} viewBox="0 0 27 36" className={styles.mobile}>
            <path d={CURVED_MOBILE_PATH} stroke={color} strokeWidth={2} />
          </svg>
          <svg {...svgProps} viewBox="0 0 28 37" className={styles.desktop}>
            <path d={CURVED_DESKTOP_PATH} stroke={color} strokeWidth={3} />
          </svg>
        </>
      )}

      {variant === 'medium' && (
        <>
          <svg {...svgProps} viewBox="0 0 33 27" className={styles.mobile}>
            <mask
              id={`${id}-m`}
              maskUnits="userSpaceOnUse"
              x="-3.99986"
              y="-6.22898"
              width="41.7529"
              height="38.3765"
              fill="black"
            >
              <rect
                fill="white"
                x="-3.99986"
                y="-6.22898"
                width="41.7529"
                height="38.3765"
              />
              <path d={MEDIUM_MOBILE_PATH} />
            </mask>
            <path d={MEDIUM_MOBILE_PATH} fill={color} />
            <path
              d={MEDIUM_MOBILE_PATH}
              stroke={color}
              strokeWidth={2}
              mask={`url(#${id}-m)`}
            />
          </svg>
          <svg {...svgProps} viewBox="0 0 34 28" className={styles.desktop}>
            <mask
              id={`${id}-d`}
              maskUnits="userSpaceOnUse"
              x="-4.90387"
              y="-5.98291"
              width="43.934"
              height="40.4126"
              fill="black"
            >
              <rect
                fill="white"
                x="-4.90387"
                y="-5.98291"
                width="43.934"
                height="40.4126"
              />
              <path d={MEDIUM_DESKTOP_PATH} />
            </mask>
            <path d={MEDIUM_DESKTOP_PATH} fill={color} />
            <path
              d={MEDIUM_DESKTOP_PATH}
              stroke={color}
              strokeWidth={3}
              mask={`url(#${id}-d)`}
            />
          </svg>
        </>
      )}
    </span>
  );
}
